import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import { List, type RecordOf } from 'immutable';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';

import CloseIcon from '@mui/icons-material/ClearRounded';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { Theme } from '@mui/material/styles';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { categoriesSelectors } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions } from 'app-components/flux/root-ui';
import {
  customFrequencyUtils,
  scheduledTransactionsActions,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import { FrequencyEnum } from 'app-components/flux/scheduled-transactions/frequency';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
  type ScheduledTransactionType,
  mkScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import type { SplitProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import type { ProjectSelect } from 'components/ClientProjectSelect';
import DevMenuButton from 'components/DevMenuButton';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import { dispatchGeneratorDialog } from 'components/Transactions/GeneratorDialog/dispatcher';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import {
  OVERFLOW_SPLIT_MEMO,
  splitToPlain,
  type PlainSplitProps,
} from 'app-components/flux/transactions/splitsHelpers';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';

import BasicTab from './tabs/basicTab';
import BillConnectTab from './tabs/billConnectTab';
import FrequencyTab from './tabs/frequencyTab';

import { matchCriteriaOptions } from './types';
import type { FormikScheduledTxn, MatchOptions, STFormikProps } from './types';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';
import Dump from 'components/Dump';

const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    display: 'flex',
    flexShrink: 0,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.border.main}`,
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
  },
  billConnect: {
    display: 'flex',
    flexDirection: 'column',
  },
  hidden: {
    display: 'none',
  },
  frequencyHeader: {
    display: 'flex',
    padding: '14px 24px',
    backgroundColor: theme.palette.background.default,
  },
  titleText: {
    display: 'flex',
    justifySelf: 'center',
    fontSize: 24,
    fontWeight: '400',
    flexGrow: 1,
    justifyContent: 'center',
    paddingLeft: '40',
  },
  closeButton: {
    display: 'flex',
    justifySelf: 'flex-end',
    color: theme.palette.text.secondary,
    flexGrow: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
}));

const stripOverflowMemoFromSplit = (splitToStrip: PlainSplitProps): PlainSplitProps => {
  const items = splitToStrip.items ?? [];
  return {
    ...splitToStrip,
    items: items.map((item) => {
      if (item.memo === OVERFLOW_SPLIT_MEMO) {
        const { memo: _memo, ...rest } = item;
        return { ...rest, isOverflow: true };
      }
      return item;
    }),
  };
};

const getInitialDate = (model: any, txn?: any, currentDate?: DateTime) => {
  const dueOnDate = model?.dueOn && DateTime.fromISO(model.dueOn);
  if (dueOnDate?.isValid) {
    return dueOnDate;
  }
  const txnDate = txn?.postedOn && DateTime.fromISO(txn.postedOn);
  if (txnDate?.isValid) {
    return txnDate;
  }
  return currentDate;
};

const getInitialAmount = (scheduledTransaction: ScheduledTransactionType) => {
  let criteria: MatchOptions;
  if (scheduledTransaction?.matchAnyAmount) criteria = matchCriteriaOptions.ANY_AMOUNT;
  else if (
    scheduledTransaction?.matchAmountVariance &&
    scheduledTransaction?.matchAmountVariance > 0 &&
    !scheduledTransaction?.resetMatchAmountVariance
  )
    criteria = matchCriteriaOptions.LIMITED_RANGE;
  else if (scheduledTransaction?.matchAmountVariance === 0 && !scheduledTransaction?.resetMatchAmountVariance)
    criteria = matchCriteriaOptions.EXACT_AMOUNT;
  else criteria = matchCriteriaOptions.AUTO_MATCH;

  return criteria;
};

// ===== Tab Panel =====
type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};
function TabPanel(props: TabPanelProps) {
  const { classes } = useStyles();
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className={value === index ? classes.tabPanel : classes.hidden}
    >
      {value === index && children}
    </div>
  );
}

// ===== Form Content =====
interface FormProps {
  formikProps: STFormikProps;
  create?: boolean;
  scheduledTransaction: scheduledTransactionsTypes.ScheduledTransactionType;
  minimumDate?: DateTime;
  subscriptionInitial?: boolean;
  tabValue: number;
  setTabValue: (_: number) => void;
  dialogActions: any;
  openAssignSplits: () => void;
}
function FormContent({
  formikProps,
  create,
  scheduledTransaction,
  minimumDate,
  subscriptionInitial,
  tabValue,
  setTabValue,
  dialogActions,
  openAssignSplits,
}: FormProps) {
  const { classes } = useStyles();

  const formModel = mkScheduledTransaction({
    ...(formikProps.values || {}),
    dueOn: formikProps.values?.dueOn?.toISODate(),
  } as any);
  // BVA needs to track form, not created model status, so it can be available on create
  const showBillTab = useAppSelector(
    (state) =>
      billPresentmentSelectors.getShowBillTab(state, scheduledTransaction as ScheduledTransaction) ||
      billPresentmentSelectors.getCanBVA(state, formModel),
  );

  const handleChange = (_e: unknown, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Form className={classes.form}>
      <Dump value={{ scheduledTransaction, formikProps }} id={scheduledTransaction?.id} />

      <Tabs value={tabValue} onChange={handleChange} aria-label='custom frequency tabs' className={classes.tabs}>
        <Tab label='Basic Details' />
        <Tab label='Frequency & Occurrence' />
        {showBillTab && <Tab label='Bill Connect' />}
      </Tabs>

      {/* Basic Details */}
      <TabPanel value={tabValue} index={0}>
        <DialogContent id='frm-root-basic'>
          <BasicTab
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            setTabValue={setTabValue}
            create={create}
            subscriptionInitial={subscriptionInitial}
            showBillConnect={showBillTab}
            onAssignSplits={openAssignSplits}
          />
        </DialogContent>
      </TabPanel>

      {/* Frequency & Occurrence */}
      <TabPanel value={tabValue} index={1}>
        <DialogContent id='frm-root-frequency'>
          <FrequencyTab formikProps={formikProps} minimumDate={minimumDate} />
        </DialogContent>
      </TabPanel>

      {/* Bill Connect */}
      {showBillTab && (
        <TabPanel value={tabValue} index={2}>
          <DialogContent className={classes.billConnect} id='frm-root-bill-connect'>
            <BillConnectTab formikProps={formikProps} />
          </DialogContent>
        </TabPanel>
      )}
      {dialogActions}
    </Form>
  );
}

type SplitDialogMode = 'transaction' | 'series';

function OpenSplitDialogOnMount(props: {
  dialogId: string;
  dirty: boolean;
  onCloseScheduledTxn: (isDirty?: boolean, forceClose?: boolean) => void;
  onBackToScheduledTxn: () => void;
  mode: SplitDialogMode;

  txnForSplitDialog: TransactionProps;
  splitRecord: RecordOf<SplitProps> | null;
  setSeriesSplit: (val: RecordOf<SplitProps> | null) => void;
  setAmount: (val: number) => void;
  setCoa: (newCOA?: chartOfAccountsTypes.ChartOfAccountProps | null) => void;
  setTags: (val: TransactionProps['tags']) => void;
  accountId: string;
  disabledPaymentFn: (bId: string) => boolean;
  setCustomersAndProjects: React.Dispatch<React.SetStateAction<ProjectSelect>>;
}): null {
  const dispatch = useAppDispatch();
  const { dialogId } = props;

  // Prevent re-dispatching on rerenders (Formik updates, new callback identities, etc.).
  const dispatchedForDialogIdRef = useRef<string | null>(null);

  // Keep a ref to the latest props so we can dispatch with the current values while
  // keeping the effect dependency surface minimal and stable.
  const latestPropsRef = useRef(props);
  latestPropsRef.current = props;

  useEffect(() => {
    if (dispatchedForDialogIdRef.current === dialogId) {
      return;
    }
    dispatchedForDialogIdRef.current = dialogId;

    const {
      dirty,
      onCloseScheduledTxn,
      onBackToScheduledTxn,
      mode,
      txnForSplitDialog,
      splitRecord,
      setSeriesSplit,
      setAmount,
      setCoa,
      setTags,
      accountId,
      disabledPaymentFn,
      setCustomersAndProjects,
    } = latestPropsRef.current;

    const closeSplitDialog = () => dispatch(rootUiActions.removeDialog(dialogId));

    dispatch(
      showSplitDialog({
        id: dialogId,
        mode,
        txn: txnForSplitDialog,
        split: splitRecord,
        setSplit: setSeriesSplit,
        setAmount,
        setCoa,
        setTags,
        accountId,
        disabledPaymentFn,
        setCustomersAndProjects,
        onBack: () => {
          closeSplitDialog();
          onBackToScheduledTxn();
        },
        onClose: () => {
          closeSplitDialog();
          onCloseScheduledTxn(dirty);
        },
      }),
    );
  }, [dispatch, dialogId]);

  return null;
}

export interface ScheduledTransactionEditDialogProps {
  open: boolean;
  title?: string;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  onClose: (isDirty?: boolean, forceClose?: boolean) => void;
  onSubmit?: () => void;
  onBack?: () => void;
  subscriptionInitial?: boolean;
  incomeInitial?: boolean;
  transferInitial?: boolean;
  fakeCreate?: boolean;
  initialTab?: number;
  /** When true, open the "Assign Splits to Series" flow immediately. */
  initialShowAssignSplits?: boolean;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

function ScheduledTransactionEditDialog({
  open,
  title = 'Edit Series',
  scheduledTransaction: scheduledTransactionProp,
  onClose, // accepts props 'dirty', and 'forceClose' in order
  onSubmit,
  onBack,
  subscriptionInitial = false,
  incomeInitial = false,
  transferInitial = false,
  fakeCreate,
  initialTab = 0,
  initialShowAssignSplits = false,
}: ScheduledTransactionEditDialogProps) {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const model = scheduledTransactionProp;
  const scheduledTransaction = (model && 'toJS' in model ? model.toJS() : model) as ScheduledTransactionType; // .toJS() turns all properties to unknown

  const create = fakeCreate || !model?.id;

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const initialDate = getInitialDate(scheduledTransaction, model.transaction, currentDate);
  const minimumDate = DateTime.min(initialDate, currentDate);

  const source = window.location.pathname.replaceAll?.('/', '');

  // @ts-expect-error FIXME TS2345
  const isDrawer = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('webDrawerInput'));
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));

  const incomeCat = useAppSelector((state) =>
    incomeInitial ? categoriesSelectors.getPersonalIncomeCategory(state) : null,
  );
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const [tabValue, setTabValue] = useState(initialTab);
  const [showAssignSplits, setShowAssignSplits] = useState(Boolean(initialShowAssignSplits && splitSeriesFf));
  const assignSplitsChangedRef = useRef(false);
  const pendingAssignSplitsSplitRef = useRef<PlainSplitProps | null | undefined>(undefined);
  const pendingAssignSplitsCoaRef = useRef<chartOfAccountsTypes.ChartOfAccountProps | null | undefined>(undefined);
  const pendingAssignSplitsTagsRef = useRef<TransactionProps['tags'] | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setShowAssignSplits(Boolean(initialShowAssignSplits && splitSeriesFf));
    }
  }, [initialShowAssignSplits, open, scheduledTransaction?.id, scheduledTransaction?.clientId, splitSeriesFf]);

  useEffect(() => {
    if (showAssignSplits) {
      assignSplitsChangedRef.current = false;
      pendingAssignSplitsSplitRef.current = undefined;
      pendingAssignSplitsCoaRef.current = undefined;
      pendingAssignSplitsTagsRef.current = undefined;
    }
  }, [showAssignSplits, scheduledTransaction?.id, scheduledTransaction?.clientId]);

  // # ===================================================================================== #
  // # -----                               Formik Setup                                ----- #
  // # ===================================================================================== #

  const initialValues: FormikScheduledTxn = useMemo(() => {
    const incomeRecurrence: RecurrenceType = {
      byDay: customFrequencyUtils.indexToWeekday(currentDate.weekday - 1),
      frequency: FrequencyEnum.WEEKLY,
      interval: 2,
    };

    const billRecurrence: RecurrenceType = {
      // For create flow, default monthly recurrence based on the start date month, never literal 29/30/31.
      byMonthDay: [customFrequencyUtils.dayOfMonthToByMonthDay(initialDate)],
      frequency: FrequencyEnum.MONTHLY,
      interval: 1,
    };

    // Sanity Check
    let isExcludedFromF2S = scheduledTransaction?.transaction?.isExcludedFromF2S;
    let isExcludedFromReports = scheduledTransaction?.transaction?.isExcludedFromReports;
    const excludeF2SDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.DEST));
    const excludeReportsDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.DEST));

    let defaultCoa =
      scheduledTransaction.transaction?.coa ||
      chartOfAccountsTypes.mkChartOfAccount({ id: '0', type: 'UNCATEGORIZED' });
    if (incomeInitial && incomeCat && incomeCat.id && create) {
      defaultCoa = { id: incomeCat.id, type: 'CATEGORY' };
    }
    if (transferInitial && create) {
      defaultCoa = { id: '2', type: 'BALANCE_ADJUSTMENT' };
      isExcludedFromReports = true;
      isExcludedFromF2S = true;
    }

    const state = store.getState();
    const spreadTxn =
      scheduledTransaction.transaction && 'toJS' in scheduledTransaction.transaction
        ? (scheduledTransaction.transaction.toJS() as TransactionProps) // .toJS() turns all properties to unknown
        : scheduledTransaction.transaction;

    const isCredit = chartOfAccountsSelectors.isCreditBill(state, spreadTxn?.accountId, defaultCoa);

    // Is transaction of type 'transfer'
    const isTransfer = chartOfAccountsUtils.isTransferCOA(defaultCoa) || isCredit;
    const seriesPrimaryCoa =
      scheduledTransactionsUtils.getSeriesPrimaryCoa(
        spreadTxn ? ({ ...spreadTxn, coa: defaultCoa } as TransactionProps) : null,
      ) ?? defaultCoa;
    const isIncome = Boolean(
      seriesPrimaryCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(state, seriesPrimaryCoa.id),
    );
    const defaultRecurrence = scheduledTransaction.recurrence || (isIncome ? incomeRecurrence : billRecurrence);

    const frequencyAlias = customFrequencyUtils.mapRecurrenceFrequencyToFrequencyAlias(defaultRecurrence);
    const endOnDate =
      scheduledTransaction.recurrence?.endOn &&
      (scheduledTransaction.recurrence.endOn instanceof DateTime
        ? scheduledTransaction.recurrence.endOn
        : DateTime.fromISO(scheduledTransaction.recurrence.endOn));
    const virtualEndOn = endOnDate && endOnDate.isValid ? endOnDate : null;

    const immutableTags =
      scheduledTransaction.transaction && scheduledTransaction.transaction.tags
        ? List(scheduledTransaction.transaction?.tags)
        : undefined;

    const formikScheduledTransaction = {
      // all modifiable props should be listed below (formik requires it)
      ...scheduledTransaction,
      dueOn: initialDate,
      transaction: {
        amount: 0,
        payee: '',
        coa: defaultCoa,
        postedOn: initialDate.toISODate(),
        ...spreadTxn,
        accountId: spreadTxn?.accountId || '',
        isExcludedFromF2S,
        isExcludedFromReports,
        excludeF2SDest,
        excludeReportsDest,
        isSubscription: Boolean(spreadTxn?.isSubscription),
        isBill: Boolean(!spreadTxn?.isSubscription && !isIncome),
        isTransfer,
        //  isCredit,
        tags: immutableTags,
      },
      virtualFrequency: frequencyAlias,
      virtualEndOn,
      virtualRecurrence: defaultRecurrence,
      matchCriteria: getInitialAmount(scheduledTransaction),
      autoAdjustAmount: scheduledTransaction.autoAdjustAmount || AutoAdjustAmountEnum.none,
      autoAdjustDueOn: Boolean(create ? false : scheduledTransaction.autoAdjustDueOn),
    };
    return formikScheduledTransaction;
  }, [currentDate, scheduledTransaction, create, incomeInitial, transferInitial, incomeCat, store, initialDate]);

  // TODO: Move to bit.dev in future during cleanup work?
  // @ts-expect-error FIXME TS7006
  const getExclusions = (values) => {
    let excludeF2S: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;
    let excludeReports: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;

    if (values?.coa?.type === 'ACCOUNT') {
      if (values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromF2S && !values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromReports && !values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    } else {
      if (values.isExcludedFromF2S) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    }

    return {
      excludeF2S,
      excludeReports,
    };
  };

  const getType = (values: TransactionProps) => {
    let type: string = 'income';
    if (values.isBill) type = 'bill';
    if (values.isSubscription) type = 'subscription';
    if (values.isTransfer) type = 'transfer';

    return type;
  };

  const formikSubmit = (values: any, actions: any, force: boolean = false) => {
    const {
      virtualRecurrence,
      dueOn,
      virtualEndOn,
      virtualFrequency,
      transaction,
      id,
      matchCriteria,
    }: FormikScheduledTxn = values || {};
    const isValidRecurrence = customFrequencyUtils.isValidRecurrence(virtualRecurrence);

    // The following set of if conditions specifically checks reqiured field values on submit.  Ideally this would be done via form field validation, but since
    // fields are on different tabs, Formik does not handle this well.  In the future, we should convert this entire form to react-hook-form and react-hook-form-mui
    const errs = { virtualRecurrence: '', dueOn: '', virtualEndOn: '' };
    if (!isValidRecurrence) {
      errs.virtualRecurrence = 'Required';
    }

    const startDateMissingMessage = !dueOn ? 'Required' : '';
    if (startDateMissingMessage.length > 0) {
      errs.dueOn = startDateMissingMessage;
    }

    const minDueOn = DateTime.min(initialValues.dueOn, currentDate);

    if (dueOn < minDueOn) {
      errs.dueOn = `Date must be between ${minDueOn.toFormat('D')}`;
    }

    if (virtualEndOn && virtualEndOn < dueOn) {
      errs.virtualEndOn = `Date must be on or after ${dueOn.toFormat('D')}`;
    }

    // if there are any errors on the given fields, set those errors and switch the correct tab.
    if (!force && (errs.virtualRecurrence !== '' || errs.dueOn !== '' || errs.virtualEndOn !== '')) {
      actions.setErrors(errs);
      actions.setSubmitting(false);
      setTabValue(1);
      return;
    }

    if (customFrequencyUtils.verifyValues(values)) {
      // both the below dates are formatted in such a way because if you send an ISO QCS sometimes won't generate models
      // for same-day because it treats them as in the past depending on the time of day
      const stDueOn = dueOn?.isValid ? dueOn.toFormat('yyyy-MM-dd') : undefined;
      // const endOn = virtualEndOn?.isValid && virtualEndOn.toFormat('yyyy-MM-dd');

      const valTxn = { ...(model?.transaction || {}), ...(transaction || {}) } as TransactionProps;
      const splitItems = valTxn.split?.items;
      const hasSplitItems = Boolean(
        splitItems && ('size' in splitItems ? splitItems.size > 0 : (splitItems?.length ?? 0) > 0),
      );
      if (hasSplitItems) {
        // When a series has assigned splits, the "main" category is not applicable.
        // Align with transaction-detail behavior (coa cleared when split exists).
        valTxn.coa = null;
        valTxn.customerId = null;
        valTxn.projectId = null;
        valTxn.isBillable = false;
      }

      // For create or edit series flow, ensure that we do not send invalid value of businessId field during QCS call
      if (valTxn.businessId === 'PERSONAL') {
        valTxn.businessId = '';
      }

      const count = virtualFrequency === 'One-time payment' ? 1 : undefined;

      const state = store.getState();
      const isCredit = valTxn && chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, valTxn.coa);
      const incomeCOAIds = categoriesSelectors.getIncomeCOAIds(state);
      const isIncome = scheduledTransactionsUtils.isIncomeSeriesFromTxn(valTxn, incomeCOAIds);

      // Is transaction of type 'transfer'
      const isTransfer = chartOfAccountsUtils.isTransferCOA(valTxn.coa) || isCredit;

      if (isIncome) {
        valTxn.isTransfer = false;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
      } else if (isTransfer) {
        valTxn.isTransfer = true;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
      }
      const { excludeF2S, excludeReports } = getExclusions(valTxn);
      const type = getType(valTxn);

      // Scheduled-series "template" transactions often don't have a real transaction id.
      // Avoid sending `transaction.id: null` in the request payload (prefer omitting the field).
      const normalizedTxn: TransactionProps = {
        ...valTxn,
        id: valTxn.id ?? undefined,
      };
      const newTxn = transactionsUtils.updateNewTags(transactionsTypes.mkCashFlowTransaction(normalizedTxn), true);
      const newRecurrence = customFrequencyUtils.recurrenceFromVirtualRecurrence(virtualRecurrence);
      const newRecurrenceRecord = scheduledTransactionsTypes.mkRecurrence({
        ...newRecurrence,
        endOn: virtualEndOn,
        count,
      });

      if (id) {
        dispatch(
          scheduledTransactionsActions.updateScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransactionPatch({
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflicts like BILL + isSubscription conflict
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
              ...(fakeCreate && { isUserVerified: true }),
            }),
            {
              ...(fakeCreate ? {} : { undo: { userMessage: 'Recurring transaction updated.' } }),
            },
          ),
        );
      } else {
        dispatch(
          scheduledTransactionsActions.createScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransaction({
              clientId: uuidv4().toUpperCase(), // backup for irresponsible developers
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflict like BILL + isSubscription conflict
              isUserVerified: true,
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
            }),
            { undo: { userMessage: 'Recurring transaction created.' } },
          ),
        );
      }
      const frequency = newRecurrence.alias?.toLowerCase().replaceAll?.('_', ' ') || 'Unknown';
      tracker.track(tracker.events.changeSeries, {
        source,
        purpose: create ? 'create' : 'edit',
        frequency,
        frequencyDetails: newRecurrence,
        type: type,
        frequencyState: newRecurrence?.alias ? 'changed' : 'default',
        matchCriteria: matchCriteria?.toLowerCase(),
        excludeFromSpendingPlan: transaction?.isExcludedFromF2S,
        excludeFromReports: transaction?.isExcludedFromReports,
        payee: transaction?.payee,
        category: transaction?.coa?.id
          ? chartOfAccountsSelectors.getCoaStringSelector(null, transaction?.coa, true)
          : 'uncategorized',
      });
      actions.setSubmitting?.(false);
      if (onSubmit) {
        onSubmit();
      } else {
        onClose(false);
      }
    } else {
      transaction && actions.setErrors?.(customFrequencyUtils.setErrorFields(transaction as TransactionProps));
      setTabValue(0);
    }
  };

  // # ===================================================================================== #
  // # -----                            Action Functions                               ----- #
  // # ===================================================================================== #
  const deleteFunction = () => {
    dispatch(
      scheduledTransactionsActions.deleteScheduledTransaction(
        scheduledTransactionsTypes.mkScheduledTransactionPatch({
          clientId: model?.clientId,
          id: model?.id,
          isDeleted: true,
        }),
        { undo: { userMessage: 'Recurring transaction deleted.' } },
      ),
    );
    onClose(false, true);
  };

  return (
    <Formik<FormikScheduledTxn>
      key={model?.id || model?.clientId}
      initialValues={initialValues}
      validateOnChange={didTrySubmit}
      validateOnBlur={false}
      onSubmit={formikSubmit}
    >
      {(formikProps) => {
        const { dirty } = formikProps || {};

        const formContent = (
          <FormContent
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            minimumDate={minimumDate}
            subscriptionInitial={subscriptionInitial}
            tabValue={tabValue}
            setTabValue={setTabValue}
            openAssignSplits={() => {
              if (splitSeriesFf) {
                setShowAssignSplits(true);
              }
            }}
            dialogActions={
              // buttons must be inside Form to function properly
              <DialogActions>
                <DevMenuButton id='recurrence-dev-menu'>
                  <MenuItem onClick={() => formikSubmit(formikProps.values, {}, true)}>Force Series Submit</MenuItem>
                  <MenuItem
                    onClick={() =>
                      dispatchRequestDialog({
                        method: 'PUT',
                        path: `/scheduled-transactions/${formikProps.values?.id}`,
                        body: JSON.stringify(formikProps.values, null, 2),
                      })
                    }
                  >
                    Manual Series API Call
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const txns = [
                        {
                          ...formikProps.values.transaction,
                          state: 'CLEARED',
                        },
                      ];
                      dispatchGeneratorDialog({ txns, start: formikProps.values.dueOn?.toISODate() });
                    }}
                  >
                    Generate Series Instances
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const currentTxn = formikProps.values.transaction;
                      const txns = [
                        {
                          ...currentTxn,
                          state: 'CLEARED',
                        },
                        {
                          ...currentTxn,
                          accountId: currentTxn.coa?.id,
                          coa: { type: 'ACCOUNT', id: currentTxn.accountId },
                          amount: -currentTxn.amount,
                          state: 'CLEARED',
                        },
                      ];
                      dispatchGeneratorDialog({ txns, start: formikProps.values.dueOn?.toISODate() });
                    }}
                  >
                    Generate Series Transfer Instances
                  </MenuItem>
                </DevMenuButton>

                <QButton
                  id='delete-recurrence'
                  variant={create ? 'outlined' : 'delete'}
                  onClick={create ? () => onClose(dirty) : deleteFunction}
                  disableOutline
                >
                  {create ? 'Cancel' : 'Delete'}
                </QButton>

                <QButton
                  id='create-update-recurrence'
                  type='submit'
                  variant='contained'
                  onClick={() => setDidTrySubmit(true)}
                >
                  {create ? 'Create' : 'Update'}
                </QButton>
              </DialogActions>
            }
            create={create}
          />
        );

        const transaction = (formikProps.values?.transaction || {}) as TransactionProps;
        const splitRecord = transaction.split
          ? (transactionsTypes.mkSplit(transaction.split as SplitProps) as RecordOf<SplitProps>)
          : null;

        const noopSetCustomersAndProjects: React.Dispatch<React.SetStateAction<ProjectSelect>> = (_value) => {
          // no-op: series split assignment does not support client/project selection
        };

        const setSeriesSplit = (val: RecordOf<SplitProps> | null) => {
          assignSplitsChangedRef.current = true;
          if (!val) {
            // Explicit clear signal for API.
            const nextSplit = { items: null } satisfies PlainSplitProps;
            pendingAssignSplitsSplitRef.current = nextSplit;
            formikProps.setFieldValue('transaction.split', nextSplit);
            return;
          }
          const rawSplit = splitToPlain(val);
          const nextSplit = rawSplit
            ? stripOverflowMemoFromSplit(rawSplit)
            : ({ items: null } satisfies PlainSplitProps);
          pendingAssignSplitsSplitRef.current = nextSplit;
          formikProps.setFieldValue('transaction.split', nextSplit);
        };

        const txnForSplitDialog: TransactionProps = {
          ...transaction,
          split: splitRecord ?? undefined,
        };

        const splitDialogLauncher =
          splitSeriesFf && showAssignSplits ? (
            <OpenSplitDialogOnMount
              dialogId={`SPLIT_DIALOG_SERIES_${scheduledTransaction?.id || scheduledTransaction?.clientId || 'new'}`}
              dirty={dirty}
              onCloseScheduledTxn={onClose}
              onBackToScheduledTxn={() => {
                // If we deep-linked directly into Assign Splits (e.g., from Edit Reminder),
                // treat "Back" as "done" and close the series dialog so the user returns
                // to the previous modal (Edit Reminder), not the Edit Series UI.
                if (initialShowAssignSplits) {
                  // If splits changed (e.g., Delete All Splits), submit the series update now.
                  if (assignSplitsChangedRef.current) {
                    const valuesToSubmit = {
                      ...formikProps.values,
                      transaction: {
                        ...formikProps.values.transaction,
                        split: pendingAssignSplitsSplitRef.current ?? formikProps.values.transaction?.split,
                        coa: pendingAssignSplitsCoaRef.current ?? formikProps.values.transaction?.coa,
                        tags: pendingAssignSplitsTagsRef.current ?? formikProps.values.transaction?.tags,
                      },
                    };
                    formikSubmit(valuesToSubmit, formikProps, true);
                    return;
                  }
                  onClose(false);
                  return;
                }
                setShowAssignSplits(false);
              }}
              mode='series'
              txnForSplitDialog={txnForSplitDialog}
              splitRecord={splitRecord}
              setSeriesSplit={setSeriesSplit}
              setAmount={(val: number) => formikProps.setFieldValue('transaction.amount', val)}
              setCoa={(newCOA) => {
                pendingAssignSplitsCoaRef.current = newCOA ?? null;
                formikProps.setFieldValue('transaction.coa', newCOA ?? null);
              }}
              setTags={(val) => {
                pendingAssignSplitsTagsRef.current = val;
                formikProps.setFieldValue('transaction.tags', val);
              }}
              accountId={transaction.accountId ?? ''}
              disabledPaymentFn={() => false}
              setCustomersAndProjects={noopSetCustomersAndProjects}
            />
          ) : null;

        if (isDrawer) {
          return (
            <>
              <Drawer
                aria-labelledby='edit-recurrence-dialog'
                open={open && !showAssignSplits}
                anchor='right'
                onClose={() => onClose(dirty)}
                PaperProps={{
                  sx: { width: '50%' },
                }}
              >
                <div className={classes.frequencyHeader}>
                  <div className={classes.titleText}>{title}</div>
                  <div className={classes.closeButton}>
                    <IconButton aria-label='Close' id='dlg-close' onClick={() => onClose(false)}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
                {formContent}
              </Drawer>
              {splitDialogLauncher}
            </>
          );
        }

        return (
          <>
            <StdDialog
              aria-labelledby='edit-recurrence-dialog'
              onBack={onBack}
              onClose={() => onClose(dirty)}
              open={open && !showAssignSplits}
              showBackButton={Boolean(onBack)}
              size='md'
              title={title}
            >
              {formContent}
            </StdDialog>
            {splitDialogLauncher}
          </>
        );
      }}
    </Formik>
  );
}

export default ScheduledTransactionEditDialog;
